import { dataConstants } from 'services/redux/data/constants'
import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, Keyboard, View } from 'react-native'
import Input from "theme/Input";
import Button from "theme/Button";
import Text from "theme/Text";
import * as Animatable from 'react-native-animatable'
import { useDispatch } from 'react-redux'
import HelperDomain from 'utils/helper-domain'
import HelperFileCache from 'utils/HelperFileCache'
import _ from 'lodash'
import { configKeys } from 'utils/config/keys'
import HelperEncryption from 'utils/helper-encryption'
import GraphqlBuilder from 'utils/request/graphql/builder'
import QuerySettings from 'graphql/queries/QuerySettings'
import { removeSettings, setData, setSettings } from 'services/redux/data/actions'
import HelperSetting from 'utils/helper-setting'
import { settingsConstants } from 'utils/constants/settings-constants'
import { MODES } from 'utils/constants/app-modes';
import i18n from 'utils/i18n'
import { styles } from "../styles/styles";
import { apiConfigPrefixes } from '_shared/config/api'
import HelperDevelopment from 'utils/helper-development'
import HelperVersion from 'utils/helper-version'
import ThemedPressable from 'components/atoms/ThemedPressable'
import HelperString from 'utils/helper-string'
import { TESTID } from '_shared/config/testID'
import AccountStorage from 'utils/accounts/AccountStorage'
import AccountList from 'components/molecules/AccountList'
import VStack from 'components/atoms/stack/VStack'
import TextDivider from 'components/atoms/TextDivider'
import LoadingComponent from 'components/atoms/Loading'
import useAccountSwitch from 'utils/accounts/use-account-switch'
import { IconSystemConstants } from 'utils/constants/IconNameConstants'
import Icon from 'components/atoms/Icon'
import { configClient, useConfigProvider } from 'components/provider/ConfigProvider'

const domainTypingTimeoutInterval = 1000;
const domainMinLength = 3;

export const DomainStatus = {
	UNKOWN: 'unknown',
	VERIFIED: 'verified',
	ERROR: 'error'
}

const Domain = ( { onDomainChange, onDomainStatusChange, onModeChange } = {} ) => {
	const dispatch = useDispatch()
	
	const domainTypingTimer = useRef( null )
	const domain = useRef( '' )
	const { client: config, call: configCall } = useConfigProvider()
	
	// Create the prefixes
	const domainPrefixes = [ apiConfigPrefixes.PRODUCTION ]
	if( HelperDevelopment.isDev() || HelperVersion.isTestVersion() ) {
		domainPrefixes.unshift( apiConfigPrefixes.TEST );
	}
	if( HelperDevelopment.isDev() ) {
		domainPrefixes.unshift( apiConfigPrefixes.DEVELOPMENT );
	}
	
	const [ domainMode, setDomainMode ] = useState( _.head( domainPrefixes ) )
	const [ domainValue, setDomainValue ] = useState( '' )
	
	const [ accounts, setAccounts ] = useState( {} )
	const { isSwitching, switchAccount } = useAccountSwitch();
	const [ domainVerifying, setDomainVerifying ] = useState( null )
	const [ domainVerified, setDomainVerified ] = useState( null )
	const [ domainStatus, setDomainStatus ] = useState( DomainStatus.UNKOWN )
	const [ domainGraphQLRunning, setDomainGraphQLRunning ] = useState( false );
	
	useEffect( () => {
		let active = true;
		AccountStorage.getAccounts().then( savedAccounts => {
			if( active ) setAccounts( savedAccounts );
		});
		return () => {
			active = false;
		};
	}, [] );

	const setDomainTypingTimer = ( timer ) => {
		domainTypingTimer.current = timer
	}
	
	const setDomain = ( newDomain ) => {
		domain.current = HelperDomain.trim( newDomain )
		
		onDomainChange( domain.current );
	}
	
	const onChange = ( domain = "" ) => {
		clearTimeout( domainTypingTimer.current );
		
		setDomain( domain );
		setDomainValue( HelperDomain.trim( domain ) );
		setDomainStatus( DomainStatus.UNKOWN );
		setDomainVerified( false );
		
		setDomainTypingTimer( setTimeout( verify, domainTypingTimeoutInterval ) );
		
		HelperFileCache.drop();
	};
	
	const isDomainValid = () => {
		return _.size( HelperDomain.trim( domain.current ) ) >= domainMinLength
	}
	
	const switchMode = () => {
		const domainIndex = _.findIndex( domainPrefixes, o => {
			return o === domainMode;
		} )
		
		const newDomainMode = _.get( domainPrefixes, (domainIndex + 1), _.head( domainPrefixes ) )
		
		setDomainMode( newDomainMode )
	}
	
	const verify = () => {
		clearTimeout( domainTypingTimer.current );

		if( !isDomainValid() ) {
			setDomainVerifying( false );
			return;
		}
		
		// If we're already verifying try again later
		if( domainGraphQLRunning ) {
			clearTimeout( domainTypingTimer.current );
			setDomainTypingTimer( setTimeout( verify, domainTypingTimeoutInterval ) );
			return;
		}
		
		// Create a valid domain name
		const validDomain = HelperDomain.valid( domain.current );
		
		// Wait for the server validation before we do other fetches
		( async() => {
			// Set the customer id in the settings
			await configCall( 'set', configKeys.CUSTOMER_ID, validDomain )
			
			// Reset key! !! important !!
			HelperEncryption.resetKey();
			
			new GraphqlBuilder()
				.addQuery( 'verify_domain', QuerySettings, {} )
				.setResultPath( [
					'verify_domain',
					'settings'
				], [] )
				.setOnStart( () => {
					setDomainVerified( false );
					setDomainVerifying( true );
					setDomainGraphQLRunning( true );
				} )
				.setOnSuccess( ( result, response ) => {
					dispatch( setSettings( result ) )
					
					if ( HelperSetting.isYes( settingsConstants.MAINTENANCE ) ) {
						dispatch( setData( dataConstants.MODE, MODES.NOTIFICATIONS ) );
					}
					
					setDomainVerified( true );
					setDomainVerifying( false );
					setDomainGraphQLRunning( false );
					setDomainStatus( DomainStatus.VERIFIED );
				} )
				.setOnError( result => {
					dispatch( removeSettings() )
					
					HelperFileCache.drop();
					
					setDomainVerifying( false );
					setDomainGraphQLRunning( false );
					setDomainStatus( DomainStatus.ERROR );
				} )
				.execute();
		})()
	};
	
	const getAccessoryLeft = () => {
		const items = [];
		
		switch( true ) {
			case domainVerified:
				items.push(
					<Icon icon={ IconSystemConstants.SUCCESS } key={ HelperString.hash( 'domain_icon_success' ) } style={ [ styles.icon, styles.iconGreen ] } />
				);
				break;
				
			case domainVerifying:
				items.push(
					<Animatable.Text
						key={ HelperString.hash( 'domain_icon_verifying' ) }
						animation={ 'rotate' }>
						<Icon icon={ IconSystemConstants.SPINNER } style={ [ styles.icon, styles.iconOrange ] } />
					</Animatable.Text>
				);
				break;
				
			case domainStatus === DomainStatus.ERROR:
				items.push(
					<Icon icon={ IconSystemConstants.WARNING } key={ HelperString.hash( 'domain_icon_warning' ) } style={ [ styles.icon, styles.iconRed ] } />
				);
				break;
				
			default:
				return null;
		}
		
		return items;
	};
	
	const getAccessoryRight = () => {
		let label = i18n.t('app_domain');
		
		const customerIdPrefix = configClient.getApiCustomerIdPrefix()
		if( customerIdPrefix.length > 0 ) {
			label = label.replace('@', '@'+customerIdPrefix.replace('_','')+'_' );
		}
		
		return (
			<ThemedPressable
				key={ HelperString.hash( 'domain_prefix' ) }
				onLongPress={ switchMode.bind( this ) }
				hitSlop={ 20 }
				style={ styles.domainSuffix }
				testID={ TESTID.AUTHENTICATION.DOMAIN_PREFIX_BUTTON }>
				
					<Text
						style={ styles.textAccessoryRight }
						category={"s1"}>
						
						{ label }
					</Text>
			</ThemedPressable>
		);
	}
	
	useEffect( () => {
		switch( domainMode ) {
			case apiConfigPrefixes.TEST:
				config.switchToTest();
				break;
				
			case apiConfigPrefixes.DEVELOPMENT:
				config.switchToDevelopment();
				break;
				
			default:
				config.switchToProduction();
		}
		
		verify();
		onModeChange( domainMode )
	}, [ domainMode ] )
	
	useEffect( onDomainStatusChange.bind( this, domainStatus ), [ domainStatus ] )

	useEffect( () => {
		return () => {
			return clearTimeout(domainTypingTimer.current);
		};
	}, [] )
	
	let errorView = null;
	if( domainStatus === DomainStatus.ERROR ) {
		errorView = (
			<View
				key={ HelperString.hash( "container_text_error" ) }
				style={ styles.errorContainer }
				accessibilityRole="alert">

				<Text style={ styles.errorText }>
					{ i18n.t( `page.authentication.domain.errors.domain_not_found` )}
				</Text>
			</View>
		)
	}
	

	return (
		<View style={ styles.itemContainer } pointerEvents={ isSwitching ? 'none' : 'auto' }>
			{ _.size( accounts ) > 0 && (
				<VStack style={staticStyles.vStackMarginBottom16} space={ 8 }>
					<TextDivider text={ i18n.t( 'pages.more.accounts.saved_accounts' ) } />
					<AccountList
						accounts={ accounts }
						switchAccount={ switchAccount }
						isMultiDomain={ true }
						isMultiDomainPrefix={ true }
					/>
				</VStack>
			) }
			
			<Input
				autoCorrect={ false }
				autoCapitalize="none"
				spellCheck={ false }
				style={ styles.textInput }
				placeholder={ i18n.t('page.authentication.domain.domain_placeholder') }
				onChangeText={ onChange }
				accessoryLeft={ getAccessoryLeft }
				accessoryRight={ getAccessoryRight }
				accessoryRightWidth={ 'auto' }
				error={ domainStatus === DomainStatus.ERROR }
				returnKeyType={ "done" }
				onSubmitEditing={ () => {
					Keyboard.dismiss();
					verify();
				} }
				accessibilityLabel={ i18n.t('page.authentication.domain.domain') }
				accessibilityHint={ i18n.t('page.authentication.domain.domain_hint') }
				testID={ TESTID.AUTHENTICATION.DOMAIN_INPUT }
			/>

			{ domainVerifying && (
				<Text style={ styles.domainStatusText } accessibilityRole="text">
					{ i18n.t('page.authentication.domain.domain_checking') }
				</Text>
			) }
			
			{ errorView }

			{ !domainVerified && (
				<Button
					style={ styles.domainContinueButton }
					disabled={ domainValue.length < domainMinLength || domainGraphQLRunning }
					isLoading={ domainVerifying }
					isLoadingText={ i18n.t('page.authentication.domain.domain_checking') }
					onPress={ verify }
					accessibilityLabel={ i18n.t('basic.continue') }>

					{ i18n.t('basic.continue') }
				</Button>
			) }
			{ isSwitching && <LoadingComponent fullscreen size="l" /> }
		</View>
	);
}


const staticStyles = StyleSheet.create({
	vStackMarginBottom16: {
		marginBottom: 16,
	},
});

export default Domain;
